/**
 * Benchmarks sectoriels BTP par métier
 * Sources : Cerfrance 2024, FFB, CAPEB, INSEE
 *
 * Ce fichier contient les données de référence pour 39 métiers BTP organisés en 14 catégories.
 *
 * LOGIQUE :
 * - Métiers SPÉCIALISÉS (plombier, électricien, etc.) → seuils spécifiques Cerfrance
 * - Métiers GÉNÉRALISTES (maçon, TCE, rénovateur, etc.) → benchmarks BTP génériques actuels
 */

// ==========================================
// TYPES
// ==========================================

export interface SeuilsMarge {
  exceptionnel: number;  // >= X% : Top 10% du secteur
  excellent: number;     // >= X% : Au-dessus moyenne
  bon: number;           // >= X% : Cible moyenne
  correct: number;       // >= X% : Minimum acceptable
  fragile: number;       // >= X% : Problématique
  critique: number;      // >= X% : Action urgente
  // < critique = Déficit
}

export interface PlageValeurs {
  min: number;
  cible: number;
  max?: number;
  excellent?: number;
}

export interface TarifsHoraires {
  province: { min: number; max: number };
  metropole: { min: number; max: number };
}

export interface Coefficient {
  min: number;
  standard: number;
  max: number;
}

export interface BenchmarkMetier {
  metier_key: string;
  metier_name: string;
  categorie_key: string;
  categorie_name: string;

  // Flag pour savoir si ce métier utilise les benchmarks génériques BTP
  utilise_benchmarks_generiques: boolean;

  // Seuils de marge spécifiques (null si utilise_benchmarks_generiques = true)
  seuils_marge: SeuilsMarge | null;

  // Marges de référence
  marges: {
    brute: PlageValeurs;
    nette: PlageValeurs;
  };

  // Ratios de référence
  ratios: {
    main_oeuvre_ca: PlageValeurs;
    materiaux_ca: PlageValeurs;
  };

  // Tarifs horaires
  tarifs_horaires: TarifsHoraires;

  // Coefficient multiplicateur
  coefficient: Coefficient;

  // Métadonnées
  source: string;
  notes: string;
}

export interface CategorieMetier {
  categorie_key: string;
  categorie_name: string;
  display_order: number;
  icon: string;
  color: string;
  market_trend: 'positive' | 'stable' | 'negative';
  market_trend_value: number;
  opportunities: string[];
  risks: string[];
}

// ==========================================
// LISTE DES MÉTIERS GÉNÉRALISTES
// Ces métiers utilisent les benchmarks BTP génériques (18 types de travaux)
// ==========================================

export const METIERS_GENERALISTES = [
  'macon',
  'coffreur_bancheur',
  'constructeur_maisons',
  'demolition',
  'renovateur_general',
  'entreprise_tce',
  'artisan_multiservices',
  'autre'
] as const;

export type MetierGeneraliste = typeof METIERS_GENERALISTES[number];

// ==========================================
// CATÉGORIES DE MÉTIERS
// ==========================================

export const CATEGORIES_METIERS: CategorieMetier[] = [
  {
    categorie_key: "entreprise_batiment",
    categorie_name: "Entreprise de bâtiment",
    display_order: 0,
    icon: "Building2",
    color: "#6366F1",
    market_trend: "stable",
    market_trend_value: 2.0,
    opportunities: ["Projets globaux", "Multi-corps d'état", "Coordination TCE"],
    risks: ["Gestion sous-traitants", "Trésorerie", "Responsabilité"]
  },
  {
    categorie_key: "plomberie_chauffage",
    categorie_name: "Plomberie & Chauffage",
    display_order: 1,
    icon: "Droplets",
    color: "#3B82F6",
    market_trend: "stable",
    market_trend_value: 2.5,
    opportunities: ["Rénovation énergétique", "PAC", "Label RGE"],
    risks: ["Pénurie MO", "Coûts matières"]
  },
  {
    categorie_key: "electricite",
    categorie_name: "Électricité",
    display_order: 2,
    icon: "Zap",
    color: "#F59E0B",
    market_trend: "positive",
    market_trend_value: 3.2,
    opportunities: ["Photovoltaïque", "IRVE", "Domotique"],
    risks: ["Pénurie composants", "Formation continue"]
  },
  {
    categorie_key: "maconnerie_gros_oeuvre",
    categorie_name: "Maçonnerie & Gros Œuvre",
    display_order: 3,
    icon: "Brick",
    color: "#8B5CF6",
    market_trend: "negative",
    market_trend_value: -6.0,
    opportunities: ["Rénovation", "Extension"],
    risks: ["Effondrement neuf", "Trésorerie"]
  },
  {
    categorie_key: "couverture_toiture",
    categorie_name: "Couverture & Toiture",
    display_order: 4,
    icon: "Home",
    color: "#EF4444",
    market_trend: "stable",
    market_trend_value: 1.5,
    opportunities: ["Isolation toiture", "Photovoltaïque"],
    risks: ["Sécurité", "Pénurie MO"]
  },
  {
    categorie_key: "peinture_revetements",
    categorie_name: "Peinture & Revêtements",
    display_order: 5,
    icon: "PaintBucket",
    color: "#EC4899",
    market_trend: "stable",
    market_trend_value: -0.5,
    opportunities: ["ITE", "Décoration intérieure"],
    risks: ["Concurrence DIY", "Pouvoir d'achat"]
  },
  {
    categorie_key: "carrelage_sols",
    categorie_name: "Carrelage & Sols",
    display_order: 6,
    icon: "Grid3x3",
    color: "#14B8A6",
    market_trend: "stable",
    market_trend_value: 1.0,
    opportunities: ["Grands formats", "Sols techniques"],
    risks: ["Import low-cost", "Délais fournisseurs"]
  },
  {
    categorie_key: "menuiserie_fermetures",
    categorie_name: "Menuiserie & Fermetures",
    display_order: 7,
    icon: "DoorOpen",
    color: "#84CC16",
    market_trend: "positive",
    market_trend_value: 5.7,
    opportunities: ["Rénovation énergétique", "Sur-mesure"],
    risks: ["Coûts bois", "Délais approvisionnement"]
  },
  {
    categorie_key: "terrassement_tp",
    categorie_name: "Terrassement & TP",
    display_order: 8,
    icon: "Tractor",
    color: "#78716C",
    market_trend: "stable",
    market_trend_value: 2.8,
    opportunities: ["Marchés publics", "Assainissement"],
    risks: ["Coûts carburant", "GNR"]
  },
  {
    categorie_key: "paysagisme",
    categorie_name: "Paysagisme & Espaces Verts",
    display_order: 9,
    icon: "TreeDeciduous",
    color: "#22C55E",
    market_trend: "positive",
    market_trend_value: 3.0,
    opportunities: ["Création jardins", "Services récurrents"],
    risks: ["Saisonnalité", "Intempéries"]
  },
  {
    categorie_key: "facade_ravalement",
    categorie_name: "Façade & Ravalement",
    display_order: 10,
    icon: "Building2",
    color: "#64748B",
    market_trend: "stable",
    market_trend_value: 1.2,
    opportunities: ["ITE obligatoire", "Copropriétés"],
    risks: ["Météo", "Échafaudages"]
  },
  {
    categorie_key: "serrurerie_metallerie",
    categorie_name: "Serrurerie & Métallerie",
    display_order: 11,
    icon: "Key",
    color: "#0EA5E9",
    market_trend: "stable",
    market_trend_value: 2.0,
    opportunities: ["Sécurité", "Urgences"],
    risks: ["Concurrence low-cost", "Image prix"]
  },
  {
    categorie_key: "isolation_rge",
    categorie_name: "Isolation & Rénovation Énergétique",
    display_order: 12,
    icon: "Thermometer",
    color: "#06B6D4",
    market_trend: "positive",
    market_trend_value: 8.5,
    opportunities: ["MaPrimeRénov'", "CEE", "Passoires thermiques"],
    risks: ["Complexité aides", "Formation RGE"]
  },
  {
    categorie_key: "autre",
    categorie_name: "Autre",
    display_order: 99,
    icon: "MoreHorizontal",
    color: "#9CA3AF",
    market_trend: "stable",
    market_trend_value: 0,
    opportunities: [],
    risks: []
  }
];

// ==========================================
// BENCHMARKS PAR MÉTIER
// ==========================================

export const BENCHMARKS_METIERS: BenchmarkMetier[] = [
  // ==========================================
  // PLOMBERIE & CHAUFFAGE (4 métiers)
  // ==========================================
  {
    metier_key: "plombier",
    metier_name: "Plombier",
    categorie_key: "plomberie_chauffage",
    categorie_name: "Plomberie & Chauffage",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 40,
      excellent: 35,
      bon: 27,
      correct: 20,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 25, cible: 35, excellent: 50 },
      nette: { min: 15, cible: 27, excellent: 40 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 22, max: 26 },
      materiaux_ca: { min: 22, cible: 30, max: 38 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 50 },
      metropole: { min: 55, max: 80 }
    },
    coefficient: { min: 1.25, standard: 1.30, max: 1.40 },
    source: "Cerfrance 2024",
    notes: "Rénovation énergétique en croissance"
  },
  {
    metier_key: "chauffagiste",
    metier_name: "Chauffagiste",
    categorie_key: "plomberie_chauffage",
    categorie_name: "Plomberie & Chauffage",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 40,
      excellent: 35,
      bon: 27,
      correct: 20,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 25, cible: 35, excellent: 50 },
      nette: { min: 15, cible: 27, excellent: 40 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 22, max: 26 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 55 },
      metropole: { min: 60, max: 85 }
    },
    coefficient: { min: 1.28, standard: 1.32, max: 1.42 },
    source: "Cerfrance 2024",
    notes: "PAC et biomasse en forte demande"
  },
  {
    metier_key: "climaticien",
    metier_name: "Climaticien",
    categorie_key: "plomberie_chauffage",
    categorie_name: "Plomberie & Chauffage",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 42,
      excellent: 35,
      bon: 30,
      correct: 22,
      fragile: 18,
      critique: 8
    },
    marges: {
      brute: { min: 28, cible: 38, excellent: 52 },
      nette: { min: 18, cible: 30, excellent: 42 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 24, max: 28 },
      materiaux_ca: { min: 28, cible: 35, max: 42 }
    },
    tarifs_horaires: {
      province: { min: 50, max: 60 },
      metropole: { min: 65, max: 90 }
    },
    coefficient: { min: 1.30, standard: 1.35, max: 1.45 },
    source: "Cerfrance 2024",
    notes: "Marché en croissance avec canicules"
  },
  {
    metier_key: "frigoriste",
    metier_name: "Frigoriste",
    categorie_key: "plomberie_chauffage",
    categorie_name: "Plomberie & Chauffage",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 45,
      excellent: 38,
      bon: 30,
      correct: 22,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 28, cible: 38, excellent: 55 },
      nette: { min: 18, cible: 30, excellent: 45 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 50, max: 65 },
      metropole: { min: 65, max: 95 }
    },
    coefficient: { min: 1.32, standard: 1.38, max: 1.50 },
    source: "Estimations marché 2024",
    notes: "Spécialisation technique, forte valeur ajoutée"
  },

  // ==========================================
  // ÉLECTRICITÉ (4 métiers)
  // ==========================================
  {
    metier_key: "electricien",
    metier_name: "Électricien",
    categorie_key: "electricite",
    categorie_name: "Électricité",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 30,
      excellent: 27,
      bon: 20,
      correct: 15,
      fragile: 10,
      critique: 3
    },
    marges: {
      brute: { min: 25, cible: 40, excellent: 60 },
      nette: { min: 10, cible: 20, excellent: 30 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 22, max: 26 },
      materiaux_ca: { min: 18, cible: 25, max: 32 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 45 },
      metropole: { min: 50, max: 65 }
    },
    coefficient: { min: 1.25, standard: 1.30, max: 1.40 },
    source: "Cerfrance 2024",
    notes: "Productivité en amélioration"
  },
  {
    metier_key: "electricien_tertiaire",
    metier_name: "Électricien tertiaire",
    categorie_key: "electricite",
    categorie_name: "Électricité",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 28,
      excellent: 23,
      bon: 18,
      correct: 12,
      fragile: 8,
      critique: 2
    },
    marges: {
      brute: { min: 22, cible: 35, excellent: 50 },
      nette: { min: 8, cible: 18, excellent: 28 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 24, max: 28 },
      materiaux_ca: { min: 20, cible: 28, max: 35 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 50 },
      metropole: { min: 55, max: 70 }
    },
    coefficient: { min: 1.25, standard: 1.30, max: 1.38 },
    source: "Cerfrance 2024",
    notes: "Marchés publics et tertiaire"
  },
  {
    metier_key: "electricien_industriel",
    metier_name: "Électricien industriel",
    categorie_key: "electricite",
    categorie_name: "Électricité",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 28,
      excellent: 22,
      bon: 16,
      correct: 10,
      fragile: 6,
      critique: 2
    },
    marges: {
      brute: { min: 20, cible: 32, excellent: 45 },
      nette: { min: 8, cible: 16, excellent: 25 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 20, cible: 28, max: 38 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 55 },
      metropole: { min: 55, max: 75 }
    },
    coefficient: { min: 1.25, standard: 1.30, max: 1.40 },
    source: "Estimations marché 2024",
    notes: "Secteur industriel, contrats maintenance"
  },
  {
    metier_key: "domoticien",
    metier_name: "Domoticien",
    categorie_key: "electricite",
    categorie_name: "Électricité",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 40,
      excellent: 33,
      bon: 28,
      correct: 20,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 30, cible: 45, excellent: 65 },
      nette: { min: 15, cible: 28, excellent: 40 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 20, cible: 30, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 55, max: 70 },
      metropole: { min: 70, max: 95 }
    },
    coefficient: { min: 1.32, standard: 1.38, max: 1.50 },
    source: "Estimations marché 2024",
    notes: "Niche en croissance, haute valeur ajoutée"
  },

  // ==========================================
  // MAÇONNERIE & GROS ŒUVRE (4 métiers) - GÉNÉRALISTES
  // ==========================================
  {
    metier_key: "macon",
    metier_name: "Maçon",
    categorie_key: "maconnerie_gros_oeuvre",
    categorie_name: "Maçonnerie & Gros Œuvre",
    utilise_benchmarks_generiques: true,
    seuils_marge: null, // Utilise les seuils génériques BTP
    marges: {
      brute: { min: 20, cible: 28, excellent: 38 },
      nette: { min: 8, cible: 12, excellent: 18 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 30, cible: 38, max: 45 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 45 },
      metropole: { min: 45, max: 60 }
    },
    coefficient: { min: 1.20, standard: 1.30, max: 1.40 },
    source: "Cerfrance 2024",
    notes: "Secteur en crise, recentrage rénovation"
  },
  {
    metier_key: "coffreur_bancheur",
    metier_name: "Coffreur-bancheur",
    categorie_key: "maconnerie_gros_oeuvre",
    categorie_name: "Maçonnerie & Gros Œuvre",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 18, cible: 25, excellent: 35 },
      nette: { min: 6, cible: 10, excellent: 16 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 32, max: 40 },
      materiaux_ca: { min: 28, cible: 35, max: 42 }
    },
    tarifs_horaires: {
      province: { min: 38, max: 48 },
      metropole: { min: 48, max: 65 }
    },
    coefficient: { min: 1.20, standard: 1.28, max: 1.38 },
    source: "Estimations marché 2024",
    notes: "Spécialisation béton armé"
  },
  {
    metier_key: "constructeur_maisons",
    metier_name: "Constructeur maisons individuelles",
    categorie_key: "maconnerie_gros_oeuvre",
    categorie_name: "Maçonnerie & Gros Œuvre",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 15, cible: 22, excellent: 32 },
      nette: { min: 5, cible: 10, excellent: 16 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 25, max: 32 },
      materiaux_ca: { min: 35, cible: 42, max: 50 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 50 },
      metropole: { min: 50, max: 70 }
    },
    coefficient: { min: 1.18, standard: 1.25, max: 1.35 },
    source: "Cerfrance 2024",
    notes: "Secteur en crise profonde, neuf -28%"
  },
  {
    metier_key: "demolition",
    metier_name: "Démolition",
    categorie_key: "maconnerie_gros_oeuvre",
    categorie_name: "Maçonnerie & Gros Œuvre",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 20, cible: 30, excellent: 42 },
      nette: { min: 8, cible: 15, excellent: 25 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 28, max: 35 },
      materiaux_ca: { min: 15, cible: 22, max: 30 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 60 },
      metropole: { min: 55, max: 80 }
    },
    coefficient: { min: 1.25, standard: 1.35, max: 1.48 },
    source: "Estimations marché 2024",
    notes: "Gestion déchets et sécurité"
  },

  // ==========================================
  // COUVERTURE & TOITURE (3 métiers)
  // ==========================================
  {
    metier_key: "couvreur",
    metier_name: "Couvreur",
    categorie_key: "couverture_toiture",
    categorie_name: "Couverture & Toiture",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 35,
      excellent: 30,
      bon: 25,
      correct: 18,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 20, cible: 30, excellent: 42 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 27, max: 32 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 50 },
      metropole: { min: 55, max: 70 }
    },
    coefficient: { min: 1.30, standard: 1.38, max: 1.48 },
    source: "Cerfrance 2024",
    notes: "Métier à risque, primes sécurité"
  },
  {
    metier_key: "couvreur_zingueur",
    metier_name: "Couvreur-zingueur",
    categorie_key: "couverture_toiture",
    categorie_name: "Couverture & Toiture",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 38,
      excellent: 32,
      bon: 26,
      correct: 18,
      fragile: 12,
      critique: 4
    },
    marges: {
      brute: { min: 22, cible: 32, excellent: 45 },
      nette: { min: 15, cible: 26, excellent: 38 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 30, max: 35 },
      materiaux_ca: { min: 28, cible: 35, max: 42 }
    },
    tarifs_horaires: {
      province: { min: 48, max: 58 },
      metropole: { min: 58, max: 75 }
    },
    coefficient: { min: 1.32, standard: 1.40, max: 1.52 },
    source: "Estimations marché 2024",
    notes: "Double compétence, tarifs premium"
  },
  {
    metier_key: "etancheur",
    metier_name: "Étancheur",
    categorie_key: "couverture_toiture",
    categorie_name: "Couverture & Toiture",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 35,
      excellent: 30,
      bon: 25,
      correct: 18,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 22, cible: 32, excellent: 45 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 25, max: 30 },
      materiaux_ca: { min: 30, cible: 38, max: 45 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 55 },
      metropole: { min: 55, max: 75 }
    },
    coefficient: { min: 1.30, standard: 1.38, max: 1.48 },
    source: "Cerfrance 2024",
    notes: "Toitures terrasses en croissance"
  },

  // ==========================================
  // PEINTURE & REVÊTEMENTS (3 métiers)
  // ==========================================
  {
    metier_key: "peintre",
    metier_name: "Peintre",
    categorie_key: "peinture_revetements",
    categorie_name: "Peinture & Revêtements",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 24,
      excellent: 20,
      bon: 17,
      correct: 14,
      fragile: 12,
      critique: 5
    },
    marges: {
      brute: { min: 45, cible: 58, excellent: 72 },
      nette: { min: 12, cible: 17, excellent: 24 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 29, max: 34 },
      materiaux_ca: { min: 15, cible: 22, max: 28 }
    },
    tarifs_horaires: {
      province: { min: 25, max: 35 },
      metropole: { min: 35, max: 50 }
    },
    coefficient: { min: 1.25, standard: 1.32, max: 1.42 },
    source: "Cerfrance 2024",
    notes: "Rentabilité 16,8% du CA en moyenne"
  },
  {
    metier_key: "peintre_decorateur",
    metier_name: "Peintre-décorateur",
    categorie_key: "peinture_revetements",
    categorie_name: "Peinture & Revêtements",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 28,
      excellent: 23,
      bon: 18,
      correct: 14,
      fragile: 10,
      critique: 4
    },
    marges: {
      brute: { min: 48, cible: 62, excellent: 75 },
      nette: { min: 14, cible: 20, excellent: 28 }
    },
    ratios: {
      main_oeuvre_ca: { min: 28, cible: 34, max: 40 },
      materiaux_ca: { min: 12, cible: 18, max: 25 }
    },
    tarifs_horaires: {
      province: { min: 30, max: 45 },
      metropole: { min: 40, max: 60 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.48 },
    source: "Estimations marché 2024",
    notes: "Prestations haut de gamme, décors"
  },
  {
    metier_key: "poseur_revetements",
    metier_name: "Poseur revêtements",
    categorie_key: "peinture_revetements",
    categorie_name: "Peinture & Revêtements",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 26,
      excellent: 22,
      bon: 18,
      correct: 14,
      fragile: 10,
      critique: 4
    },
    marges: {
      brute: { min: 42, cible: 55, excellent: 68 },
      nette: { min: 12, cible: 18, excellent: 26 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 30, max: 36 },
      materiaux_ca: { min: 18, cible: 25, max: 32 }
    },
    tarifs_horaires: {
      province: { min: 28, max: 38 },
      metropole: { min: 38, max: 52 }
    },
    coefficient: { min: 1.25, standard: 1.32, max: 1.42 },
    source: "Estimations marché 2024",
    notes: "Sols souples, papiers peints"
  },

  // ==========================================
  // CARRELAGE & SOLS (2 métiers)
  // ==========================================
  {
    metier_key: "carreleur",
    metier_name: "Carreleur",
    categorie_key: "carrelage_sols",
    categorie_name: "Carrelage & Sols",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 35,
      excellent: 30,
      bon: 25,
      correct: 18,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 40, cible: 55, excellent: 70 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 30, max: 35 },
      materiaux_ca: { min: 20, cible: 28, max: 35 }
    },
    tarifs_horaires: {
      province: { min: 30, max: 40 },
      metropole: { min: 40, max: 55 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.45 },
    source: "Estimations marché 2024",
    notes: "Grands formats = tarifs plus élevés"
  },
  {
    metier_key: "solier_moquettiste",
    metier_name: "Solier-moquettiste",
    categorie_key: "carrelage_sols",
    categorie_name: "Carrelage & Sols",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 32,
      excellent: 26,
      bon: 20,
      correct: 15,
      fragile: 10,
      critique: 4
    },
    marges: {
      brute: { min: 38, cible: 50, excellent: 65 },
      nette: { min: 12, cible: 20, excellent: 30 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 22, cible: 30, max: 38 }
    },
    tarifs_horaires: {
      province: { min: 28, max: 38 },
      metropole: { min: 38, max: 52 }
    },
    coefficient: { min: 1.25, standard: 1.32, max: 1.42 },
    source: "Estimations marché 2024",
    notes: "Sols souples, moquettes, LVT"
  },

  // ==========================================
  // MENUISERIE & FERMETURES (3 métiers)
  // ==========================================
  {
    metier_key: "menuisier",
    metier_name: "Menuisier",
    categorie_key: "menuiserie_fermetures",
    categorie_name: "Menuiserie & Fermetures",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 35,
      excellent: 30,
      bon: 25,
      correct: 18,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 50, cible: 61, excellent: 72 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 23, max: 28 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 50 },
      metropole: { min: 50, max: 65 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.45 },
    source: "Cerfrance 2024",
    notes: "Résultat +14% en 2024"
  },
  {
    metier_key: "menuisier_agenceur",
    metier_name: "Menuisier-agenceur",
    categorie_key: "menuiserie_fermetures",
    categorie_name: "Menuiserie & Fermetures",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 38,
      excellent: 32,
      bon: 26,
      correct: 20,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 52, cible: 65, excellent: 78 },
      nette: { min: 18, cible: 28, excellent: 38 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 26, max: 32 },
      materiaux_ca: { min: 22, cible: 28, max: 35 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 58 },
      metropole: { min: 55, max: 75 }
    },
    coefficient: { min: 1.30, standard: 1.38, max: 1.50 },
    source: "Estimations marché 2024",
    notes: "Agencement sur-mesure, valeur ajoutée"
  },
  {
    metier_key: "poseur_menuiseries",
    metier_name: "Poseur menuiseries",
    categorie_key: "menuiserie_fermetures",
    categorie_name: "Menuiserie & Fermetures",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 32,
      excellent: 26,
      bon: 22,
      correct: 15,
      fragile: 12,
      critique: 4
    },
    marges: {
      brute: { min: 35, cible: 48, excellent: 62 },
      nette: { min: 12, cible: 22, excellent: 32 }
    },
    ratios: {
      main_oeuvre_ca: { min: 28, cible: 35, max: 42 },
      materiaux_ca: { min: 15, cible: 22, max: 28 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 45 },
      metropole: { min: 45, max: 60 }
    },
    coefficient: { min: 1.25, standard: 1.32, max: 1.42 },
    source: "Estimations marché 2024",
    notes: "RGE indispensable pour aides"
  },

  // ==========================================
  // TERRASSEMENT & TP (2 métiers)
  // ==========================================
  {
    metier_key: "terrassier",
    metier_name: "Terrassier",
    categorie_key: "terrassement_tp",
    categorie_name: "Terrassement & TP",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 22,
      excellent: 18,
      bon: 15,
      correct: 10,
      fragile: 8,
      critique: 2
    },
    marges: {
      brute: { min: 20, cible: 30, excellent: 42 },
      nette: { min: 8, cible: 15, excellent: 22 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 23, max: 28 },
      materiaux_ca: { min: 15, cible: 22, max: 30 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 60 },
      metropole: { min: 55, max: 75 }
    },
    coefficient: { min: 1.25, standard: 1.32, max: 1.42 },
    source: "Cerfrance 2024",
    notes: "Attention coûts carburant élevés"
  },
  {
    metier_key: "canalisateur",
    metier_name: "Canalisateur",
    categorie_key: "terrassement_tp",
    categorie_name: "Terrassement & TP",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 22,
      excellent: 18,
      bon: 14,
      correct: 10,
      fragile: 6,
      critique: 2
    },
    marges: {
      brute: { min: 18, cible: 28, excellent: 40 },
      nette: { min: 6, cible: 14, excellent: 22 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 26, max: 32 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 42, max: 55 },
      metropole: { min: 52, max: 70 }
    },
    coefficient: { min: 1.22, standard: 1.30, max: 1.40 },
    source: "Estimations marché 2024",
    notes: "Réseaux humides, marchés publics"
  },

  // ==========================================
  // PAYSAGISME (2 métiers)
  // ==========================================
  {
    metier_key: "paysagiste",
    metier_name: "Paysagiste",
    categorie_key: "paysagisme",
    categorie_name: "Paysagisme & Espaces Verts",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 40,
      excellent: 32,
      bon: 28,
      correct: 20,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 30, cible: 42, excellent: 58 },
      nette: { min: 15, cible: 28, excellent: 40 }
    },
    ratios: {
      main_oeuvre_ca: { min: 28, cible: 35, max: 42 },
      materiaux_ca: { min: 15, cible: 22, max: 30 }
    },
    tarifs_horaires: {
      province: { min: 30, max: 40 },
      metropole: { min: 40, max: 55 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.48 },
    source: "Cerfrance 2024",
    notes: "Création 57% du CA, entretien 40%"
  },
  {
    metier_key: "elagueur",
    metier_name: "Élagueur",
    categorie_key: "paysagisme",
    categorie_name: "Paysagisme & Espaces Verts",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 48,
      excellent: 40,
      bon: 35,
      correct: 25,
      fragile: 20,
      critique: 8
    },
    marges: {
      brute: { min: 40, cible: 55, excellent: 70 },
      nette: { min: 20, cible: 35, excellent: 48 }
    },
    ratios: {
      main_oeuvre_ca: { min: 35, cible: 42, max: 50 },
      materiaux_ca: { min: 8, cible: 12, max: 18 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 60 },
      metropole: { min: 55, max: 80 }
    },
    coefficient: { min: 1.35, standard: 1.45, max: 1.60 },
    source: "Estimations marché 2024",
    notes: "Spécialisation très rentable, métier à risque"
  },

  // ==========================================
  // FAÇADE & RAVALEMENT (2 métiers)
  // ==========================================
  {
    metier_key: "facadier",
    metier_name: "Façadier",
    categorie_key: "facade_ravalement",
    categorie_name: "Façade & Ravalement",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 35,
      excellent: 28,
      bon: 25,
      correct: 18,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 30, cible: 45, excellent: 60 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 20, cible: 28, max: 35 }
    },
    tarifs_horaires: {
      province: { min: 30, max: 45 },
      metropole: { min: 40, max: 55 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.45 },
    source: "Estimations marché 2024",
    notes: "ITE obligatoire >50% surface = opportunité"
  },
  {
    metier_key: "enduiseur",
    metier_name: "Enduiseur",
    categorie_key: "facade_ravalement",
    categorie_name: "Façade & Ravalement",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 32,
      excellent: 26,
      bon: 22,
      correct: 16,
      fragile: 12,
      critique: 4
    },
    marges: {
      brute: { min: 28, cible: 42, excellent: 55 },
      nette: { min: 12, cible: 22, excellent: 32 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 32, max: 40 },
      materiaux_ca: { min: 22, cible: 30, max: 38 }
    },
    tarifs_horaires: {
      province: { min: 28, max: 40 },
      metropole: { min: 38, max: 52 }
    },
    coefficient: { min: 1.25, standard: 1.32, max: 1.42 },
    source: "Estimations marché 2024",
    notes: "Enduits traditionnels et monocouche"
  },

  // ==========================================
  // SERRURERIE & MÉTALLERIE (3 métiers)
  // ==========================================
  {
    metier_key: "serrurier",
    metier_name: "Serrurier",
    categorie_key: "serrurerie_metallerie",
    categorie_name: "Serrurerie & Métallerie",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 45,
      excellent: 38,
      bon: 30,
      correct: 22,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 35, cible: 50, excellent: 68 },
      nette: { min: 15, cible: 30, excellent: 45 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 23, max: 28 },
      materiaux_ca: { min: 18, cible: 25, max: 32 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 60 },
      metropole: { min: 55, max: 85 }
    },
    coefficient: { min: 1.30, standard: 1.40, max: 1.55 },
    source: "Estimations marché 2024",
    notes: "Urgences très rentables"
  },
  {
    metier_key: "metallier",
    metier_name: "Métallier",
    categorie_key: "serrurerie_metallerie",
    categorie_name: "Serrurerie & Métallerie",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 38,
      excellent: 30,
      bon: 25,
      correct: 18,
      fragile: 12,
      critique: 4
    },
    marges: {
      brute: { min: 32, cible: 45, excellent: 60 },
      nette: { min: 12, cible: 25, excellent: 38 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 55 },
      metropole: { min: 50, max: 70 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.48 },
    source: "Estimations marché 2024",
    notes: "Ouvrages métalliques, structures"
  },
  {
    metier_key: "ferronnier",
    metier_name: "Ferronnier",
    categorie_key: "serrurerie_metallerie",
    categorie_name: "Serrurerie & Métallerie",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 42,
      excellent: 35,
      bon: 28,
      correct: 20,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 38, cible: 52, excellent: 68 },
      nette: { min: 18, cible: 30, excellent: 42 }
    },
    ratios: {
      main_oeuvre_ca: { min: 30, cible: 38, max: 45 },
      materiaux_ca: { min: 18, cible: 25, max: 32 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 65 },
      metropole: { min: 55, max: 85 }
    },
    coefficient: { min: 1.35, standard: 1.45, max: 1.60 },
    source: "Estimations marché 2024",
    notes: "Ferronnerie d'art = premium"
  },

  // ==========================================
  // ISOLATION & RGE (3 métiers)
  // ==========================================
  {
    metier_key: "isolateur",
    metier_name: "Isolateur",
    categorie_key: "isolation_rge",
    categorie_name: "Isolation & Rénovation Énergétique",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 32,
      excellent: 26,
      bon: 22,
      correct: 16,
      fragile: 12,
      critique: 4
    },
    marges: {
      brute: { min: 30, cible: 45, excellent: 62 },
      nette: { min: 12, cible: 22, excellent: 32 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 28, cible: 35, max: 42 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 45 },
      metropole: { min: 45, max: 60 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.45 },
    source: "Estimations marché 2024",
    notes: "MaPrimeRénov' = volume assuré"
  },
  {
    metier_key: "plaquiste",
    metier_name: "Plaquiste",
    categorie_key: "isolation_rge",
    categorie_name: "Isolation & Rénovation Énergétique",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 35,
      excellent: 28,
      bon: 25,
      correct: 18,
      fragile: 15,
      critique: 5
    },
    marges: {
      brute: { min: 35, cible: 50, excellent: 65 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 32, max: 40 },
      materiaux_ca: { min: 22, cible: 28, max: 35 }
    },
    tarifs_horaires: {
      province: { min: 30, max: 40 },
      metropole: { min: 40, max: 55 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.45 },
    source: "Estimations marché 2024",
    notes: "Isolation intérieure + cloisons"
  },
  {
    metier_key: "facadier_ite",
    metier_name: "Façadier ITE",
    categorie_key: "isolation_rge",
    categorie_name: "Isolation & Rénovation Énergétique",
    utilise_benchmarks_generiques: false,
    seuils_marge: {
      exceptionnel: 32,
      excellent: 26,
      bon: 22,
      correct: 16,
      fragile: 12,
      critique: 4
    },
    marges: {
      brute: { min: 30, cible: 45, excellent: 60 },
      nette: { min: 12, cible: 22, excellent: 32 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 32, max: 40 },
      materiaux_ca: { min: 30, cible: 38, max: 45 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 48 },
      metropole: { min: 45, max: 62 }
    },
    coefficient: { min: 1.30, standard: 1.38, max: 1.50 },
    source: "Estimations marché 2024",
    notes: "ITE en forte croissance, RGE obligatoire"
  },

  // ==========================================
  // ENTREPRISE DE BÂTIMENT (3 métiers) - GÉNÉRALISTES
  // Utilisent les benchmarks BTP génériques par défaut
  // ==========================================
  {
    metier_key: "renovateur_general",
    metier_name: "Rénovateur général",
    categorie_key: "entreprise_batiment",
    categorie_name: "Entreprise de bâtiment",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 35, cible: 50, excellent: 68 },
      nette: { min: 15, cible: 26, excellent: 38 }
    },
    ratios: {
      main_oeuvre_ca: { min: 22, cible: 28, max: 35 },
      materiaux_ca: { min: 22, cible: 30, max: 38 }
    },
    tarifs_horaires: {
      province: { min: 40, max: 55 },
      metropole: { min: 50, max: 72 }
    },
    coefficient: { min: 1.30, standard: 1.38, max: 1.50 },
    source: "Estimations marché 2024",
    notes: "Coordination = valeur ajoutée"
  },
  {
    metier_key: "entreprise_tce",
    metier_name: "Entreprise générale TCE",
    categorie_key: "entreprise_batiment",
    categorie_name: "Entreprise de bâtiment",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 30, cible: 42, excellent: 55 },
      nette: { min: 12, cible: 20, excellent: 30 }
    },
    ratios: {
      main_oeuvre_ca: { min: 18, cible: 24, max: 30 },
      materiaux_ca: { min: 25, cible: 32, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 45, max: 60 },
      metropole: { min: 55, max: 80 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.48 },
    source: "Estimations marché 2024",
    notes: "Gestion multi-lots, sous-traitance"
  },
  {
    metier_key: "artisan_multiservices",
    metier_name: "Artisan multiservices",
    categorie_key: "entreprise_batiment",
    categorie_name: "Entreprise de bâtiment",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 35, cible: 48, excellent: 62 },
      nette: { min: 15, cible: 25, excellent: 35 }
    },
    ratios: {
      main_oeuvre_ca: { min: 25, cible: 32, max: 40 },
      materiaux_ca: { min: 18, cible: 25, max: 32 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 48 },
      metropole: { min: 45, max: 62 }
    },
    coefficient: { min: 1.28, standard: 1.35, max: 1.45 },
    source: "Estimations marché 2024",
    notes: "Polyvalence, petits travaux"
  },

  // ==========================================
  // AUTRE (1 métier) - GÉNÉRALISTE
  // ==========================================
  {
    metier_key: "autre",
    metier_name: "Autre métier BTP",
    categorie_key: "autre",
    categorie_name: "Autre",
    utilise_benchmarks_generiques: true,
    seuils_marge: null,
    marges: {
      brute: { min: 25, cible: 40, excellent: 55 },
      nette: { min: 10, cible: 20, excellent: 30 }
    },
    ratios: {
      main_oeuvre_ca: { min: 20, cible: 28, max: 35 },
      materiaux_ca: { min: 20, cible: 30, max: 40 }
    },
    tarifs_horaires: {
      province: { min: 35, max: 50 },
      metropole: { min: 45, max: 65 }
    },
    coefficient: { min: 1.25, standard: 1.30, max: 1.40 },
    source: "Moyennes BTP France 2024",
    notes: "Valeurs génériques secteur BTP"
  }
];

// ==========================================
// FONCTIONS UTILITAIRES
// ==========================================

/**
 * Récupère un benchmark par clé métier
 */
export function getBenchmarkMetier(metierKey: string): BenchmarkMetier | undefined {
  return BENCHMARKS_METIERS.find(b => b.metier_key === metierKey);
}

/**
 * Récupère tous les métiers d'une catégorie
 */
export function getMetiersParCategorie(categorieKey: string): BenchmarkMetier[] {
  return BENCHMARKS_METIERS.filter(b => b.categorie_key === categorieKey);
}

/**
 * Récupère une catégorie par clé
 */
export function getCategorieMetier(categorieKey: string): CategorieMetier | undefined {
  return CATEGORIES_METIERS.find(c => c.categorie_key === categorieKey);
}

/**
 * Vérifie si un métier utilise les benchmarks génériques BTP
 */
export function isMetierGeneraliste(metierKey: string): boolean {
  return METIERS_GENERALISTES.includes(metierKey as MetierGeneraliste);
}

/**
 * Récupère les seuils de marge pour un métier
 * Si métier généraliste, retourne null (utiliser les seuils globaux de calculs-rentabilite.ts)
 */
export function getSeuilsMargeMetier(metierKey: string): SeuilsMarge | null {
  const benchmark = getBenchmarkMetier(metierKey);
  if (!benchmark || benchmark.utilise_benchmarks_generiques) {
    return null;
  }
  return benchmark.seuils_marge;
}

/**
 * Récupère la liste des métiers pour un select (groupés par catégorie)
 */
export function getMetiersGroupesParCategorie(): Array<{
  categorie: CategorieMetier;
  metiers: BenchmarkMetier[];
}> {
  return CATEGORIES_METIERS
    .filter(cat => cat.categorie_key !== 'autre') // Exclure "Autre" du groupement
    .map(categorie => ({
      categorie,
      metiers: getMetiersParCategorie(categorie.categorie_key)
    }))
    .filter(group => group.metiers.length > 0)
    .concat([{
      categorie: CATEGORIES_METIERS.find(c => c.categorie_key === 'autre')!,
      metiers: getMetiersParCategorie('autre')
    }]);
}
